import { useState, useCallback, useEffect, useRef } from 'react';
//-tech-debt-react-query;
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  QueryClient,
} from '@tanstack/react-query';
import { fetchItem, postItem } from '../support/queries';
import { CacheKey } from '../types/cache-keys';
import {
  DeploymentResult,
  EntityType,
  SnackableCourse,
  VersionScope,
} from '../types/snackableCourse';

// ============================================================================
// ASYNC JOB TYPES
// ============================================================================

interface JobStatus {
  id: number;
  job_id: string;
  job_type: string;
  status: 'pending' | 'running' | 'processing' | 'completed' | 'failed';
  result?: any;
  error_message?: string;
  created_at?: string;
  started_at?: string;
  completed_at?: string;
  tasks?: Array<{
    id: number;
    task_id: string;
    task_type: string;
    status: string;
    result?: any;
    error_message?: string;
  }>;
  payload?: any;
}

export interface SubmitSnackableCourseJobPayload extends VersionScope {
  entity_type: EntityType;
  entity_id: number;
  regenerate?: boolean;
  force_new?: boolean;
  additional_context?: string;
  initial?: boolean;
}

export interface SnackableCourseExistsData extends VersionScope {
  exists: boolean;
  course_id?: number;
  staged_moodle_id?: number;
  deployed_moodle_id?: number;
  staged_course?: any;
  deployed_course?: any;
  is_generating?: boolean;

  // Resource-generation/documentation job
  active_job_id?: string | null;
  active_job_status?: string | null;

  // Course-generation job
  active_course_job_id?: string | null;
  active_course_job_status?: string | null;

  version_context?: VersionScope | null;
}

// ============================================================================
// VERSION SCOPE HELPERS
// ============================================================================

const cleanVersionScope = (scope?: VersionScope | null): VersionScope => {
  if (!scope) return {};

  return {
    product_id: scope.product_id ?? undefined,
    product_version_id: scope.product_version_id ?? undefined,
    product_license_id: scope.product_license_id ?? undefined,
    doc_version: scope.doc_version ?? undefined,
    deployment_scope: scope.deployment_scope ?? undefined,
    generation_scope: scope.generation_scope ?? undefined,
    version_source: scope.version_source ?? undefined,
  };
};

const versionScopeQueryKey = (scope?: VersionScope | null) => {
  const cleaned = cleanVersionScope(scope);
  return JSON.stringify({
    product_id: cleaned.product_id ?? null,
    product_version_id: cleaned.product_version_id ?? null,
    product_license_id: cleaned.product_license_id ?? null,
    doc_version: cleaned.doc_version ?? null,
    deployment_scope: cleaned.deployment_scope ?? null,
    generation_scope: cleaned.generation_scope ?? null,
  });
};

const withVersionScopeParams = (
  params: Record<string, any>,
  versionScope?: VersionScope | null
) => {
  return {
    ...params,
    ...cleanVersionScope(versionScope),
  };
};

// ============================================================================
// POLLING HELPERS
// ============================================================================

const POLL_BASE_MS = 15_000;
const POLL_MAX_MS = 60_000;
const POLL_MULTIPLIER = 1.5;

function nextPollMs(current: number) {
  return Math.min(Math.floor(current * POLL_MULTIPLIER), POLL_MAX_MS);
}

function withJitter(ms: number) {
  const jitter = Math.floor(ms * 0.1);
  const delta = Math.floor(Math.random() * (jitter * 2 + 1)) - jitter;
  return Math.max(5_000, ms + delta);
}

function getRetryAfterMs(err: any): number | null {
  const retryAfter =
    err?.response?.headers?.['retry-after'] ??
    err?.response?.headers?.['Retry-After'];

  if (!retryAfter) return null;

  const sec = Number(retryAfter);
  if (!Number.isNaN(sec)) return Math.max(1_000, sec * 1000);

  const dateMs = Date.parse(retryAfter);
  if (!Number.isNaN(dateMs)) return Math.max(1_000, dateMs - Date.now());

  return null;
}

function isTerminal(status?: string) {
  return status === 'completed' || status === 'failed';
}

// ============================================================================
// JOIN-TABLE CACHE INVALIDATION
// ============================================================================

const PARENT_ENTITY_KEYS: CacheKey[] = [
  CacheKey.Product,
  CacheKey.ProductFeature,
  CacheKey.ProductComponent,
  CacheKey.ProductUseCase,
  CacheKey.ProductVersion,
  CacheKey.GtmSolution,
  CacheKey.GtmSolutionComponent,
  CacheKey.GtmSolutionUseCase,
  CacheKey.GtmSolutionUseCaseComponent,
  CacheKey.GoToMarket,
  CacheKey.SecurityUseCase,
  CacheKey.SecurityUseCaseComponent,
];

const RESOURCE_JOIN_KEYS: CacheKey[] = [
  CacheKey.Resource,
  CacheKey.ProductToResource,
  CacheKey.ProductFeatureToResource,
  CacheKey.ProductComponentToResource,
  CacheKey.ProductUseCaseToResource,
  CacheKey.GtmSolutionToResource,
  CacheKey.GtmSolutionComponentToResource,
  CacheKey.GtmSolutionUseCaseToResource,
  CacheKey.GtmSolutionUseCaseComponentToResource,
  CacheKey.GoToMarketToResource,
  CacheKey.SecurityUseCaseToResource,
  CacheKey.SecurityUseCaseComponentToResource,
];

const TAG_JOIN_KEYS: CacheKey[] = [
  CacheKey.Tag,
  CacheKey.ProductToTag,
  CacheKey.ProductFeatureToTag,
  CacheKey.ProductComponentToTag,
  CacheKey.ProductUseCaseToTag,
  CacheKey.GtmSolutionToTag,
  CacheKey.GtmSolutionComponentToTag,
  CacheKey.GtmSolutionUseCaseToTag,
  CacheKey.GtmSolutionUseCaseComponentToTag,
  CacheKey.GoToMarketToTag,
  CacheKey.SecurityUseCaseToTag,
  CacheKey.SecurityUseCaseComponentToTag,
];

function invalidateJoinTableCaches(queryClient: QueryClient) {
  [...PARENT_ENTITY_KEYS, ...RESOURCE_JOIN_KEYS, ...TAG_JOIN_KEYS].forEach(
    (key) => {
      queryClient.invalidateQueries({ queryKey: [key] });
    }
  );
}

// ============================================================================
// SYNCHRONOUS COURSE QUERIES
// ============================================================================

export function useSnackableCourseExists(
  entityType: EntityType,
  entityId: number | null,
  versionScope?: VersionScope | null,
  options?: Omit<
    UseQueryOptions<SnackableCourseExistsData>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<SnackableCourseExistsData>({
    queryKey: [
      CacheKey.SnackableCourseExists,
      entityType,
      entityId,
      versionScopeQueryKey(versionScope),
    ],
    queryFn: () =>
      fetchItem(
        'ai/snackable-course/exists',
        withVersionScopeParams(
          {
            entity_type: entityType,
            entity_id: entityId,
          },
          versionScope
        )
      ),
    ...{
        enabled: !!entityId && entityId > 0,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        ...options,
      },
  });
}

export function useFetchSnackableCourse(
  entityType: EntityType,
  entityId: number | null,
  versionScope?: VersionScope | null
) {
  return useQuery<SnackableCourse>({
    queryKey: [
      CacheKey.SnackableCourse,
      entityType,
      entityId,
      versionScopeQueryKey(versionScope),
    ],
    queryFn: () =>
      fetchItem(
        'ai/snackable-course/retrieve',
        withVersionScopeParams(
          {
            entity_type: entityType,
            entity_id: entityId,
          },
          versionScope
        )
      ),
    ...{
        enabled: !!entityId && entityId > 0,
        staleTime: 60_000,
      },
  });
}

export function useDeployCourseMutation() {
  const queryClient = useQueryClient();

  return useMutation<DeploymentResult, Error, { course_id: number; to_staging: boolean }>({
    mutationFn: (params) => postItem('moodle/cyber-academy/course/deploy', params),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourse] });
      queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseStatus] });

      if (data.course_id) {
        const existingData = queryClient.getQueryData<any>([
          CacheKey.SnackableCourseExists,
          data.reference_type,
          data.reference_id,
        ]);

        if (existingData) {
          queryClient.setQueryData(
            [CacheKey.SnackableCourseExists, data.reference_type, data.reference_id],
            {
              ...existingData,
              [data.deployment_type === 'staging' ? 'staged_moodle_id' : 'deployed_moodle_id']:
                data.courseid,
            }
          );
        }
      }
    },
  });
}

export function useFetchDeploymentStatusByEntity(
  entityType: EntityType,
  entityId: number | null,
  versionScope?: VersionScope | null
) {
  return useQuery({
    queryKey: [
      CacheKey.SnackableCourseStatus,
      entityType,
      entityId,
      versionScopeQueryKey(versionScope),
    ],
    queryFn: () =>
      fetchItem(
        'moodle/cyber-academy/course/deployment-status',
        withVersionScopeParams(
          {
            entity_type: entityType,
            entity_id: entityId,
          },
          versionScope
        )
      ),
    ...{
        enabled: !!entityId && entityId > 0,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
      },
  });
}

// ============================================================================
// ASYNC COURSE GENERATION
// ============================================================================

export function useSnackableCourseAsync() {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const pollMsRef = useRef<number>(POLL_BASE_MS);
  const pollingRef = useRef(false);
  const pollOnceRef = useRef<(jid: string) => Promise<void>>();

  const isActive = submitting || polling;

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    clearTimer();
    jobIdRef.current = null;
    pollingRef.current = false;
    setPolling(false);
  }, [clearTimer]);

  const reset = useCallback(() => {
    stopPolling();
    setJobId(null);
    setJobStatus(null);
    setSubmitting(false);
    setError(null);
    pollMsRef.current = POLL_BASE_MS;
  }, [stopPolling]);

  const scheduleNext = useCallback(
    (jid: string, ms: number) => {
      clearTimer();
      timeoutRef.current = setTimeout(() => {
        void pollOnceRef.current?.(jid);
      }, withJitter(ms));
    },
    [clearTimer]
  );

  const pollOnce = useCallback(
    async (jid: string) => {
      try {
        const status: JobStatus = await fetchItem(
          `ai/snackable-course/status/${jid}`,
          {}
        );
        setJobStatus(status);

        if (isTerminal(status.status)) {
          setJobId(null);
          stopPolling();
          setSubmitting(false);

          if (status.status === 'completed') {
            queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourse] });
            queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseExists] });
            queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseStatus] });
            invalidateJoinTableCaches(queryClient);
          } else {
            setError(status.error_message || 'Job failed');
          }
          return;
        }

        pollMsRef.current = nextPollMs(pollMsRef.current);
        scheduleNext(jid, pollMsRef.current);
      } catch (err: any) {
        const retryAfterMs = getRetryAfterMs(err);
        if (retryAfterMs) {
          pollMsRef.current = Math.max(pollMsRef.current, retryAfterMs);
          scheduleNext(jid, pollMsRef.current);
          return;
        }

        const errorMsg =
          err.response?.data?.error ||
          err.message ||
          'Failed to poll job status';
        setError(errorMsg);
        setSubmitting(false);
        setJobId(null);
        stopPolling();
      }
    },
    [queryClient, scheduleNext, stopPolling]
  );

  useEffect(() => {
    pollOnceRef.current = pollOnce;
  }, [pollOnce]);

  const startPolling = useCallback(
    (jid: string) => {
      if (!jid) return;
      if (jobIdRef.current === jid && pollingRef.current) return;

      clearTimer();
      jobIdRef.current = jid;
      pollingRef.current = true;
      setPolling(true);
      setSubmitting(false);
      pollMsRef.current = POLL_BASE_MS;

      void pollOnce(jid);
    },
    [clearTimer, pollOnce]
  );

  const submitJob = useCallback(
    async (payload: SubmitSnackableCourseJobPayload) => {
      try {
        setError(null);
        setJobStatus(null);
        setSubmitting(true);

        const response = await postItem('ai/snackable-course/submit', payload);

        setJobId(response.job_id);
        return response;
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.error || err.message || 'Failed to submit job';
        setSubmitting(false);
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    },
    []
  );

  const resumePolling = useCallback(
    (jid: string) => {
      if (!jid) return;
      setError(null);
      setJobId(jid);
      startPolling(jid);
    },
    [startPolling]
  );

  useEffect(() => {
    if (jobId) startPolling(jobId);
  }, [jobId, startPolling]);

  useEffect(() => {
    return () => {
      clearTimer();
    };
  }, [clearTimer]);

  const cancelPolling = useCallback(() => {
    reset();
  }, [reset]);

  return {
    submitJob,
    resumePolling,
    cancelPolling,
    reset,
    jobId,
    jobStatus,
    polling,
    submitting,
    isActive,
    error,
  };
}

// ============================================================================
// ENTITY RESOURCE CHECK
// ============================================================================

export interface EntityResourcesStatus extends VersionScope {
  entity_type: string;
  entity_id: number;
  has_all_resources: boolean;
  resource_count: number;
  required_count: number;
  existing_types: string[];
  missing_types: string[];
  is_generating: boolean;
  active_job_id: string | null;
  active_job_status: string | null;
  active_job_queued_at: string | null;
  version_context?: VersionScope | null;
}

export function useEntityResourcesCheck(
  entityType: EntityType | string,
  entityId: number | null,
  versionScope?: VersionScope | null
) {
  const query = useQuery<EntityResourcesStatus>({
    queryKey: [
      CacheKey.EntityResourcesCheck,
      entityType,
      entityId,
      versionScopeQueryKey(versionScope),
    ],
    queryFn: () =>
      fetchItem(
        'ai/entity/resources/check',
        withVersionScopeParams(
          {
            entity_type: entityType,
            entity_id: entityId,
          },
          versionScope
        )
      ),
    ...{
        enabled: !!entityId && entityId > 0,
        staleTime: 20_000,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        refetchInterval: (query) => {
          if (query.state.data?.is_generating) return 20_000;
          return false;
        },
        refetchIntervalInBackground: false,
      },
  });

  return query;
}

export function useEntityDocumentationAsync() {
  const queryClient = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [polling, setPolling] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jobIdRef = useRef<string | null>(null);
  const pollMsRef = useRef<number>(POLL_BASE_MS);
  const pollingRef = useRef(false);
  const pollOnceRef = useRef<(jid: string) => Promise<void>>();

  const isActive = submitting || polling;

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    clearTimer();
    jobIdRef.current = null;
    pollingRef.current = false;
    setPolling(false);
  }, [clearTimer]);

  const reset = useCallback(() => {
    stopPolling();
    setJobId(null);
    setJobStatus(null);
    setSubmitting(false);
    setError(null);
    pollMsRef.current = POLL_BASE_MS;
  }, [stopPolling]);

  const scheduleNext = useCallback(
    (jid: string, ms: number) => {
      clearTimer();
      timeoutRef.current = setTimeout(() => {
        void pollOnceRef.current?.(jid);
      }, withJitter(ms));
    },
    [clearTimer]
  );

  const pollOnce = useCallback(
    async (jid: string) => {
      try {
        const status: JobStatus = await fetchItem(
          `ai/entity/documentation/job-status/${jid}`,
          {}
        );
        setJobStatus(status);

        if (isTerminal(status.status)) {
          setJobId(null);
          stopPolling();

          if (status.status === 'completed') {
            queryClient.invalidateQueries({ queryKey: [CacheKey.EntityResourcesCheck] });
            queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourse] });
            queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseExists] });
            queryClient.invalidateQueries({ queryKey: [CacheKey.SnackableCourseStatus] });
            invalidateJoinTableCaches(queryClient);
          } else {
            setError(status.error_message || 'Job failed');
          }
          return;
        }

        pollMsRef.current = nextPollMs(pollMsRef.current);
        scheduleNext(jid, pollMsRef.current);
      } catch (err: any) {
        const retryAfterMs = getRetryAfterMs(err);
        if (retryAfterMs) {
          pollMsRef.current = Math.max(pollMsRef.current, retryAfterMs);
          scheduleNext(jid, pollMsRef.current);
          return;
        }

        const errorMsg =
          err.response?.data?.error ||
          err.message ||
          'Failed to poll job status';
        setError(errorMsg);
        setJobId(null);
        stopPolling();
      }
    },
    [queryClient, scheduleNext, stopPolling]
  );

  useEffect(() => {
    pollOnceRef.current = pollOnce;
  }, [pollOnce]);

  const startPolling = useCallback(
    (jid: string) => {
      if (!jid) return;
      if (jobIdRef.current === jid && pollingRef.current) return;

      clearTimer();
      jobIdRef.current = jid;
      pollingRef.current = true;
      setPolling(true);
      setSubmitting(false);
      pollMsRef.current = POLL_BASE_MS;

      void pollOnce(jid);
    },
    [clearTimer, pollOnce]
  );

  const submitJob = useCallback(
    async (
      entityType: EntityType | string,
      entityId: number,
      versionScope?: VersionScope | null,
      forceRegenerate?: boolean,
      skipExistingRelationships?: boolean
    ) => {
      try {
        setError(null);
        setJobStatus(null);
        setSubmitting(true);

        const response = await postItem('ai/entity/documentation/submit', {
          entity_type: entityType,
          entity_id: entityId,
          initial: true,
          force_regenerate: forceRegenerate ?? false,
          skip_existing_relationships: skipExistingRelationships ?? true,
          ...cleanVersionScope(versionScope),
        });

        setJobId(response.job_id);
        return response;
      } catch (err: any) {
        const errorMsg =
          err.response?.data?.error || err.message || 'Failed to submit job';
        setSubmitting(false);
        setError(errorMsg);
        throw new Error(errorMsg);
      }
    },
    []
  );

  useEffect(() => {
    if (jobId) startPolling(jobId);
  }, [jobId, startPolling]);

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  const cancelPolling = useCallback(() => {
    reset();
  }, [reset]);

  return {
    submitJob,
    cancelPolling,
    reset,
    jobId,
    jobStatus,
    polling,
    submitting,
    isActive,
    error,
  };
}